"use client";
import { AutoComplete, Spin } from "antd";
import type { AutoCompleteProps } from "antd";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

type FetchMethod = "GET" | "POST";

interface AppAutoCompleteProps
  extends Omit<
    AutoCompleteProps,
    "options" | "loading" | "onSelect" | "onSearch" | "value" | "onChange" | "dataSource"
  > {
  // Fetching props
  url?: string;
  method?: FetchMethod;
  body?: Record<string, unknown>;
  headers?: Record<string, string>;
  dataSource?: Record<string, unknown>[];
  // Server-side search. The typed text goes out as `?<searchParam>=…` after
  // debounceMs, so the whole table is searched without ever loading it.
  searchParam?: string;
  debounceMs?: number;
  /**
   * Characters required before anything is fetched. Defaults to 1, so an
   * untouched field costs no request — the first page of an unfiltered table is
   * an arbitrary slice, not a useful suggestion. Set 0 to preload on mount for
   * a small table where that first page really is the whole list.
   */
  minChars?: number;
  // Transform props
  labelField?: string | ((item: Record<string, unknown>) => string);
  valueField?: string;
  // Labels for ids this component never searched for — e.g. a value set by a
  // barcode scan. Without it such a value has no text to show.
  labelMap?: Record<string, string>;
  /**
   * Fields whose exact value should bind a row without the user picking it —
   * e.g. ["id", "email"]. A scanned or pasted identifier is unambiguous, so
   * making the librarian click a one-row dropdown is pure friction.
   *
   * Deliberately opt-in and exact: auto-selecting on "the search narrowed to
   * one row" would bind a row mid-word, while the user is still typing.
   * valueField and the visible label are always matched too.
   */
  matchFields?: string[];
  // Form wiring — `value` is the id, never the visible text.
  value?: any;
  onChange?: (value: any, option: any, fullItem?: Record<string, unknown>) => void;
  onSelect?: (value: any, option: any, fullItem: Record<string, unknown>) => void;
  onSourceLoaded?: (data: Record<string, unknown>[]) => Record<string, unknown>[] | void;
}

// -- fetch reducer ---------------------------------------------------------

type FetchState = {
  data: Record<string, unknown>[];
  loading: boolean;
  error: string | null;
};

type FetchAction =
  | { type: "LOADING" }
  | { type: "SUCCESS"; payload: Record<string, unknown>[] }
  | { type: "ERROR"; payload: string };

function fetchReducer(state: FetchState, action: FetchAction): FetchState {
  switch (action.type) {
    case "LOADING":
      return { ...state, loading: true, error: null };
    case "SUCCESS":
      return { data: action.payload, loading: false, error: null };
    case "ERROR":
      return { ...state, loading: false, error: action.payload };
  }
}

function extractArray(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) return raw as Record<string, unknown>[];
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    for (const key of ["data", "items", "rows", "results"]) {
      if (Array.isArray(obj[key])) return obj[key] as Record<string, unknown>[];
    }
  }
  return [];
}

function withParam(url: string, param: string, value: string) {
  return `${url}${url.includes("?") ? "&" : "?"}${param}=${encodeURIComponent(value)}`;
}

// -- component ---------------------------------------------------------------

function AppAutoComplete({
  url,
  method = "GET",
  body,
  headers,
  dataSource,
  labelField,
  valueField = "id",
  searchParam = "q",
  debounceMs = 300,
  minChars = 1,
  labelMap,
  matchFields,
  value,
  onChange,
  onSelect,
  onSourceLoaded,
  ...props
}: AppAutoCompleteProps) {
  const [{ data: fetchedData, loading }, dispatch] = useReducer(fetchReducer, {
    data: [],
    loading: false,
    error: null,
  });

  // What the box shows. Kept separate from `value` (the id) so the user sees a
  // label, not a uuid — AutoComplete otherwise renders its value verbatim.
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");

  const bodyRef = useRef(body);
  const headersRef = useRef(headers);
  const onSourceLoadedRef = useRef(onSourceLoaded);
  // Form.Item hands down a fresh onChange each render; kept in refs so the
  // auto-select effect below doesn't re-run on every render.
  const onChangeRef = useRef(onChange);
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    bodyRef.current = body;
    headersRef.current = headers;
    onSourceLoadedRef.current = onSourceLoaded;
    onChangeRef.current = onChange;
    onSelectRef.current = onSelect;
  });

  const toLabel = useCallback(
    (item: Record<string, unknown>) =>
      typeof labelField === "function"
        ? labelField(item)
        : String(item[labelField || "name"] ?? ""),
    [labelField]
  );

  // Accumulates id -> label across searches. A later search replaces `items`,
  // so without this the selected row's label would vanish from the box.
  const labelCache = useRef(new Map<string, string>());

  // Debounced server search.
  useEffect(() => {
    if (!url || dataSource) return;
    if (query.length < minChars) {
      // Drop the previous query's rows, or clearing the box would leave its
      // results sitting in the dropdown under an empty search.
      dispatch({ type: "SUCCESS", payload: [] });
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      dispatch({ type: "LOADING" });
      try {
        const isPost = method === "POST";
        const res = await fetch(query ? withParam(url, searchParam, query) : url, {
          method,
          signal: controller.signal,
          headers: {
            ...(isPost ? { "Content-Type": "application/json" } : {}),
            ...headersRef.current,
          },
          body: isPost && bodyRef.current ? JSON.stringify(bodyRef.current) : undefined,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
        dispatch({ type: "SUCCESS", payload: extractArray(await res.json()) });
      } catch (e) {
        if ((e as { name?: string }).name !== "AbortError") {
          dispatch({ type: "ERROR", payload: String(e) });
        }
      }
    }, debounceMs);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [url, dataSource, query, minChars, method, searchParam, debounceMs]);

  const items = useMemo(() => {
    const raw = dataSource ?? fetchedData;
    return onSourceLoadedRef.current ? (onSourceLoadedRef.current(raw) ?? raw) : raw;
  }, [dataSource, fetchedData]);

  const itemMap = useMemo(
    () => new Map(items.map((item) => [String(item[valueField] ?? ""), item])),
    [items, valueField]
  );

  const options = useMemo(
    () =>
      items.map((item) => {
        const id = String(item[valueField] ?? "");
        const text = toLabel(item);
        labelCache.current.set(id, text);
        return {
          value: id,
          // The dropdown is only as wide as the input, and antd ellipsizes an
          // option to one line — which cuts a long title off mid-word. Wrapping
          // keeps the whole thing readable; `title` still gives a hover tooltip.
          label: (
            <span style={{ whiteSpace: "normal", wordBreak: "break-word" }} title={text}>
              {text}
            </span>
          ),
        };
      }),
    [items, valueField, toLabel]
  );

  // Distinguishes "the user is typing" from "the value changed underneath us".
  // Typing releases the bound id, which lands here as value -> null; without
  // this the sync below would treat that as an external clear and wipe the
  // keystroke that caused it.
  const typing = useRef(false);

  // The single place a row becomes the bound value, shared by picking an option
  // and by the exact-match auto-select — so both leave identical state behind.
  const commit = useCallback(
    (id: string, option: any) => {
      typing.current = false;
      const fullItem = itemMap.get(String(id));
      // From the cache, not option.label — that is a ReactNode, and the input's
      // value has to be a plain string.
      setText(labelCache.current.get(String(id)) ?? String(id));
      onChangeRef.current?.(id, option, fullItem);
      onSelectRef.current?.(id, option, fullItem!);
    },
    [itemMap]
  );

  // Bind a row the moment the typed text *is* an identifier for it, so a
  // scanned uuid or a pasted email needs no click. Runs only while the user is
  // typing, and only on an exact hit — a prefix that happens to narrow to one
  // row is not a decision the user has made yet.
  const matchKey = (matchFields ?? []).join(" ");
  const matchFieldsRef = useRef(matchFields);
  matchFieldsRef.current = matchFields;

  useEffect(() => {
    if (!typing.current) return;
    const needle = query.trim().toLowerCase();
    if (!needle) return;

    const eq = (v: unknown) => String(v ?? "").trim().toLowerCase() === needle;
    const hits = items.filter(
      (item) =>
        eq(item[valueField]) ||
        eq(toLabel(item)) ||
        (matchFieldsRef.current ?? []).some((field) => eq(item[field]))
    );
    // Exactly one, or it isn't unambiguous — two editions can share a title,
    // and silently binding whichever sorted first would issue the wrong copy.
    if (hits.length !== 1) return;

    const id = String(hits[0][valueField] ?? "");
    if (String(value ?? "") === id) return;
    commit(id, { value: id, label: toLabel(hits[0]) });
    // matchKey stands in for matchFields: callers pass an inline array, whose
    // identity changes every render and would re-run this effect each time.
  }, [items, query, value, valueField, matchKey, toLabel, commit]);

  // Keep the box in sync when `value` is set from outside (form reset, a scan,
  // an edit form loading its row) rather than by the user picking an option.
  useEffect(() => {
    if (labelMap) {
      for (const [id, label] of Object.entries(labelMap)) labelCache.current.set(id, label);
    }
    if (value == null || value === "") {
      if (!typing.current) setText("");
      return;
    }
    typing.current = false;
    const id = String(value);
    const known = labelCache.current.get(id) ?? (itemMap.has(id) ? toLabel(itemMap.get(id)!) : undefined);
    if (known) setText(known);
  }, [value, itemMap, toLabel, labelMap]);

  return (
    <AutoComplete
      allowClear
      style={{ width: "100%" }}
      {...props}
      value={text}
      options={options}
      // The server already filtered; filtering again would drop valid rows.
      filterOption={false}
      notFoundContent={
        loading ? (
          <Spin size="small" />
        ) : query.length < minChars ? (
          // Distinct from "no matches" — nothing has been searched for yet, and
          // antd's default empty state would claim the table is empty.
          <span style={{ color: "#999", fontSize: 12 }}>Type to search…</span>
        ) : (
          props.notFoundContent
        )
      }
      onSearch={(input) => {
        typing.current = true;
        setText(input);
        setQuery(input);
        // Typing after a selection invalidates it — clear the bound id so the
        // form can't submit a stale pick alongside unrelated visible text.
        if (value) onChange?.(undefined, null);
      }}
      onSelect={(id: string, option: any) => commit(id, option)}
      onClear={() => {
        typing.current = false;
        setText("");
        setQuery("");
        onChange?.(undefined, null);
      }}
    />
  );
}

export { AppAutoComplete };
